import { addOwnerName, checkOwnerName, strings, text } from "@quickpoint.me/shared";
import { useEffect, useRef, useState } from "react";
import { useOwnerNameAvailability } from "./useOwnerNameAvailability";
import { useRecoilState, useRecoilValue } from "recoil";
import {
	CreatingFirebaseAccount,
	FirebaseAccountCreated,
	IsCheckingOwnerName,
	NetworkError,
	OwnerNameExists,
} from "../../../state/authAtoms";
import { AuthPillButton, Banner, StyledError, StyledInfo } from "../styled/Components";

import { getAuth } from "firebase/auth";
import { useForm } from "react-hook-form";
import Turnstile from "react-turnstile";
import { getTurnstileSiteKey } from "../../../lib/getTurnstileSiteKey";
import {
	clearOwnerNameFromSessionStorage,
	setOwnerNameToSessionStorage,
} from "../../../lib/ownerNameSessionStorage";
import { IsInPortrait } from "../../../state/selectors";
import { StyledForm } from "../LoginWithEmail";
import { StyledInput } from "../styled/StyledInput";
import { StyledWrapper } from "../styled/StyledWrapper";
import { TermsOfService } from "./SignUp";

interface RegisterOwnerNameFormValues {
	ownerName: string;
}
function RegisterOwnerName() {
	const [createFirebaseAccount, setCreatingFirebaseAccount] = useRecoilState(
		CreatingFirebaseAccount,
	);
	const [accountCreated, setFirebaseAccountCreated] = useRecoilState(
		FirebaseAccountCreated,
	);
	const [isCheckingOwnerName, setIsCheckingOwnerName] =
		useRecoilState(IsCheckingOwnerName);
	const [networkError, setNetworkError] = useRecoilState(NetworkError);
	const [ownerNameExists, setOwnerNameExists] = useRecoilState(OwnerNameExists);
	const {
		watch,
		register,
		formState: { errors },
		handleSubmit,
	} = useForm<RegisterOwnerNameFormValues>();
	const formRef = useRef<HTMLFormElement>(null);
	const isInPortrait = useRecoilValue(IsInPortrait);
	const ownerName = watch("ownerName");
	const [turnstileToken, setTurnstileToken] = useState<string>();
	const [disabled, setDisabled] = useState(true);
	const nameAvailability = useOwnerNameAvailability(ownerName);

	// a stale "name taken" from an earlier submit clears as soon as the user edits
	useEffect(() => {
		setOwnerNameExists(false);
	}, [ownerName, setOwnerNameExists]);

	const submit = () => {
		if (formRef.current) {
			formRef.current.dispatchEvent(
				new Event("submit", { cancelable: true, bubbles: true }),
			);
		}
	};

	// the user is firebase-authenticated but unregistered, so the only way
	// back out of this screen is to sign out again
	const backToStart = async () => {
		const auth = getAuth();
		await auth.signOut();
		clearOwnerNameFromSessionStorage();
		window.location.reload();
	};

	async function onSubmit({ ownerName }: RegisterOwnerNameFormValues) {
		ownerName = ownerName.toLowerCase().trim();
		setIsCheckingOwnerName(true);
		if (!turnstileToken) return;
		// advisory availability check — /register below is the authority
		const checkName = await checkOwnerName(ownerName);

		if (checkName.networkError) {
			setNetworkError(true);
			setIsCheckingOwnerName(false);
			return;
		}

		if (!checkName.success) {
			setIsCheckingOwnerName(false);
			setOwnerNameExists(true);
			return;
		}

		setOwnerNameExists(false);
		setIsCheckingOwnerName(false);
		setCreatingFirebaseAccount(true);
		// the user is already authenticated; just claim the name
		const auth = getAuth();
		const user = auth.currentUser;
		if (user) {
			const jwt = await user.getIdToken();
			const addOwner = await addOwnerName({
				owner: ownerName,
				jwt,
				turnstileToken,
			});

			if (addOwner.success) {
				setCreatingFirebaseAccount(false);
				setFirebaseAccountCreated(true);
				setOwnerNameToSessionStorage(ownerName);
				// reset the whole kaboodle.
				setTimeout(() => {
					window.location.reload();
				}, 100);
			} else {
				// name lost between check and register, or the account already has one
				setCreatingFirebaseAccount(false);
				if (addOwner.message === strings.workers.ownerExists) {
					setOwnerNameExists(true);
				} else {
					setNetworkError(true);
				}
			}
		}
	}

	return (
		<StyledInput $isInPortrait={isInPortrait}>
			<Banner />
			<StyledInfo className="info pick-account-name">
				<p>{text.auth.pickNamePrompt}</p>
			</StyledInfo>
			<div className="form">
				<StyledForm>
					<div className="content">
						<form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
							<StyledWrapper>
								<label className="owner-name-label" htmlFor="ownerName">
									{text.auth.nameLabel}
								</label>
								<input
									aria-invalid={!!errors.ownerName}
									placeholder={text.auth.namePlaceholder}
									className="auth-input auth-owner-name-input input-box-as-line"
									autoComplete="off"
									inputMode="text"
									autoCorrect="off"
									spellCheck={false}
									onKeyDown={(e) => {
										if (e.key === "Enter") e.preventDefault();
									}}
									id="ownerName"
									type="text"
									{...register("ownerName", {
										required: true,
										maxLength: 100,
										minLength: 3,
										pattern: /^[a-zA-Z0-9_-]*$/i,
									})}
								/>
							</StyledWrapper>
							<StyledError role="alert">
								{errors.ownerName && errors.ownerName.type === "minLength" && (
									<span>{text.auth.nameTooShort}</span>
								)}
								{errors.ownerName && errors.ownerName.type === "maxLength" && (
									<span role="alert">{text.auth.nameTooLong}</span>
								)}
								{errors.ownerName && errors.ownerName.type === "pattern" && (
									<span role="alert">
										{text.auth.nameInvalidChars}
									</span>
								)}
							</StyledError>
							<StyledError role="alert">
								{isCheckingOwnerName && (
									<span>
										{text.auth.checkingName}
									</span>
								)}
								{createFirebaseAccount && (
									<span>{text.auth.nameAvailable}</span>
								)}
								{accountCreated && <span>{text.auth.accountReady}</span>}
								{(ownerNameExists || nameAvailability === "taken") && (
									<span>
										{text.auth.nameTaken}
									</span>
								)}
								{!ownerNameExists &&
									!isCheckingOwnerName &&
									!createFirebaseAccount &&
									!accountCreated && (
										<>
											{nameAvailability === "checking" && (
												<span aria-live="polite">{text.auth.checkingName}</span>
											)}
											{nameAvailability === "available" && (
												<span aria-live="polite">{text.auth.nameFree}</span>
											)}
										</>
									)}
							</StyledError>
						</form>
					</div>
				</StyledForm>
				{networkError && (
					<StyledError>
						{text.auth.registerError}
					</StyledError>
				)}
			</div>

			<StyledInfo className="more-info">
				<p>
					{text.auth.storiesUrlPrefix}
					<span className="pick-account-name-live">{ownerName}</span>
				</p>
			</StyledInfo>
			<div className="buttons">
				<Turnstile
					style={{
						transition: disabled ? "none" : "opacity 1s ease-out",
						opacity: disabled ? 1 : 0.3,
					}}
					sitekey={getTurnstileSiteKey()}
					theme="light"
					fixedSize={true}
					retry="auto"
					refreshExpired="auto"
					onVerify={(token) => {
						setTurnstileToken(token);
						setDisabled(false);
					}}
					onError={() => {
						// transient challenge failures (common under Safari ITP) —
						// drop the token and let retry="auto" re-run the challenge
						setTurnstileToken(undefined);
						setDisabled(true);
					}}
					onExpire={() => {
						setTurnstileToken(undefined);
						setDisabled(true);
					}}
				/>
				<AuthPillButton
					id="auth-owner-name-button"
					disabled={disabled || nameAvailability === "taken"}
					onClick={submit}
				>
					{text.auth.register}
				</AuthPillButton>
				<AuthPillButton $variant="primaryOutline" onClick={backToStart}>
					{text.auth.back}
				</AuthPillButton>
				<TermsOfService />
			</div>
		</StyledInput>
	);
}

export default RegisterOwnerName;
